import { useEffect, useMemo, useState } from 'react';
import {
  EventUserEntity,
  EventUserStatus,
  EventUserStatusLabel,
  useFindEventUsersQuery,
  useRemoveEventUserMutation,
  useRemoveEventUsersMutation,
  useUpdateEventUsersMutation,
} from 'src/store/eventApi';
import { Button } from 'src/ui/Button';
import { Checkbox } from 'src/ui/Checkbox';
import { Badge } from 'src/ui/Badge';
import { Spinner } from 'src/ui/Spinner';
import { EmptyState } from 'src/ui/EmptyState';
import { Modal } from 'src/ui/Modal';
import { Dropdown } from 'src/ui/Dropdown';
import { MenuItem } from 'src/ui/Dropdown';
import { Icon } from 'src/ui/Icon';
import { Tooltip } from 'src/ui/Tooltip';
import { NEW_EVENT_ID } from './index';
import AddAttendeesModal from './addAttendeesModal';
import AttendeeDetailModal from './attendeeDetailModal';
import {
  GENDER_FEMALE,
  GENDER_MALE,
  GENDER_NON_BINARY,
  GenderTag,
} from './genderStyle';
import { assignSeats } from './seatNumbers';
import { getAttendeeDisplayName } from 'src/util';

const STATUS_COLOR: Record<EventUserStatus, string> = {
  [EventUserStatus.REGISTERED]: 'gray',
  [EventUserStatus.CONFIRMED]: 'green',
  [EventUserStatus.CANCELLED]: 'red',
  [EventUserStatus.ATTENDED]: 'blue',
  [EventUserStatus.INVITED]: 'purple',
  [EventUserStatus.WAITLISTED]: 'orange',
};

/**
 * Drives both the stats row and the bulk status menu. ATTENDED is absent on
 * purpose: nothing in the product writes it, and picking it out of this menu
 * only removed that guest from matching. The server rejects it now; the badge
 * colour stays defined so a row left behind by an older deployment still
 * renders.
 */
const STATUS_ORDER: EventUserStatus[] = [
  EventUserStatus.INVITED,
  EventUserStatus.REGISTERED,
  EventUserStatus.WAITLISTED,
  EventUserStatus.CONFIRMED,
  EventUserStatus.CANCELLED,
];

const ageFromBirthday = (birthday: unknown): number | null => {
  if (typeof birthday !== 'string' || !birthday) return null;
  const dob = new Date(birthday);
  if (isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
  return age >= 0 && age < 120 ? age : null;
};

interface AttendeesProps {
  eventId: string | null;
}

const Attendees = ({ eventId }: AttendeesProps) => {
  const isEmpty = !eventId;
  const isNew = eventId === NEW_EVENT_ID;
  const skip = isEmpty || isNew;
  const safeEventId = eventId ?? '';

  const { data: attendees, isFetching } = useFindEventUsersQuery(safeEventId, {
    skip,
  });
  const [removeEventUser] = useRemoveEventUserMutation();
  const [removeEventUsers, removeUsersStatus] = useRemoveEventUsersMutation();
  const [updateEventUsers, updateUsersStatus] = useUpdateEventUsersMutation();

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [viewEventUserId, setViewEventUserId] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  // Reset selection when switching events
  useEffect(() => {
    setSelectedIds(new Set());
  }, [eventId]);

  // Drop stale selections after per-row delete
  useEffect(() => {
    if (!attendees) return;
    const live = new Set(attendees.map((a) => a.id));
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set<number>();
      prev.forEach((id) => {
        if (live.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [attendees]);

  const stats = useMemo(() => {
    const list = attendees ?? [];
    const status: Record<EventUserStatus, number> = {
      [EventUserStatus.REGISTERED]: 0,
      [EventUserStatus.CONFIRMED]: 0,
      [EventUserStatus.CANCELLED]: 0,
      [EventUserStatus.ATTENDED]: 0,
      [EventUserStatus.INVITED]: 0,
      [EventUserStatus.WAITLISTED]: 0,
    };
    let f = 0;
    let m = 0;
    let nb = 0;
    const ages: number[] = [];
    list.forEach((eu) => {
      if (eu.status in status) status[eu.status as EventUserStatus] += 1;
      const g = eu.user?.profile?.gender;
      if (g === GENDER_FEMALE) f += 1;
      else if (g === GENDER_MALE) m += 1;
      else if (g === GENDER_NON_BINARY) nb += 1;
      const age = ageFromBirthday(eu.user?.profile?.birthday);
      if (age !== null) ages.push(age);
    });
    const genderTotal = f + m + nb;
    const pct = (n: number) =>
      genderTotal === 0 ? 0 : Math.round((n / genderTotal) * 100);
    return {
      total: list.length,
      status,
      gender: { f, m, nb, pf: pct(f), pm: pct(m), pnb: pct(nb) },
      age: {
        count: ages.length,
        min: ages.length ? Math.min(...ages) : null,
        max: ages.length ? Math.max(...ages) : null,
        avg: ages.length
          ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length)
          : null,
      },
    };
  }, [attendees]);

  const seatMap = useMemo(() => assignSeats(attendees ?? []), [attendees]);

  const sortedAttendees = useMemo(() => {
    const arr = [...(attendees ?? [])];
    arr.sort((a, b) => {
      const sa = seatMap.get(a.userId)?.seat;
      const sb = seatMap.get(b.userId)?.seat;
      if (!sa && !sb) return 0;
      if (!sa) return 1;
      if (!sb) return -1;
      return sa.localeCompare(sb);
    });
    return arr;
  }, [attendees, seatMap]);

  if (isEmpty) {
    return (
      <div
        style={{
          padding: 16,
          color: 'var(--text-muted)',
          fontSize: 'var(--fs-sm)',
        }}
      >
        Select an event to manage attendees.
      </div>
    );
  }
  if (isNew) {
    return (
      <div
        style={{
          padding: 16,
          color: 'var(--text-muted)',
          fontSize: 'var(--fs-sm)',
        }}
      >
        Save the event first to add attendees.
      </div>
    );
  }

  const list = sortedAttendees;
  const allSelected = list.length > 0 && selectedIds.size === list.length;
  const indeterminate = selectedIds.size > 0 && selectedIds.size < list.length;

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(list.map((eu) => eu.id)));
  const clearAll = () => setSelectedIds(new Set());

  const onSetStatus = async (status: EventUserStatus) => {
    if (selectedIds.size === 0) return;
    await updateEventUsers({
      eventId: safeEventId,
      ids: Array.from(selectedIds),
      status,
    });
    setSelectedIds(new Set());
  };

  const onConfirmRemove = async () => {
    if (selectedIds.size === 0) return;
    await removeEventUsers({
      eventId: safeEventId,
      ids: Array.from(selectedIds),
    });
    setSelectedIds(new Set());
    setRemoveConfirmOpen(false);
  };

  const excludeUserIds = list.map((a) => a.userId).filter(Boolean) as string[];
  const busy =
    isFetching || removeUsersStatus.isLoading || updateUsersStatus.isLoading;

  return (
    <div className="col" style={{ height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div
        style={{
          padding: '10px 16px',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div
          className="row"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <div className="row gap-2">
            <span style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>
              Attendees
            </span>
            <span
              style={{
                fontSize: 'var(--fs-xs)',
                fontWeight: 600,
                color: 'var(--text-muted)',
                background: 'var(--bg-muted)',
                borderRadius: 99,
                padding: '1px 7px',
              }}
            >
              {stats.total}
            </span>
            {busy && <Spinner size="sm" />}
          </div>
          <Button
            variant="primary"
            size="sm"
            leftIcon="plus"
            onClick={() => setAddOpen(true)}
          >
            Add attendees
          </Button>
        </div>

        {/* Stats row */}
        <div
          className="row gap-3"
          style={{
            marginTop: 8,
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-secondary)',
            flexWrap: 'wrap',
          }}
        >
          <div className="row gap-1">
            {STATUS_ORDER.map((key) => (
              <Badge key={key} color={STATUS_COLOR[key]} size="sm">
                {EventUserStatusLabel[key]} {stats.status[key] ?? 0}
              </Badge>
            ))}
          </div>
          <div className="row gap-2">
            <span>
              ♀︎ {stats.gender.f} ({stats.gender.pf}%)
            </span>
            <span>
              ♂︎ {stats.gender.m} ({stats.gender.pm}%)
            </span>
            {stats.gender.nb > 0 && (
              <span>
                ⚧︎ {stats.gender.nb} ({stats.gender.pnb}%)
              </span>
            )}
          </div>
          {stats.age.count > 0 && (
            <span>
              Age {stats.age.min}–{stats.age.max} · avg {stats.age.avg}
            </span>
          )}
        </div>
      </div>

      {/* Bulk-select toolbar */}
      {list.length > 0 && (
        <div
          style={{
            padding: '8px 16px',
            background: 'var(--bg-muted)',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <div className="row gap-3" style={{ alignItems: 'center' }}>
            <Checkbox
              checked={allSelected || indeterminate}
              onChange={() => (allSelected ? clearAll() : selectAll())}
            />
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}>
              {selectedIds.size === 0
                ? 'None selected'
                : `${selectedIds.size} selected`}
            </span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={selectAll}
              style={{ fontSize: 'var(--fs-xs)', padding: '2px 6px' }}
            >
              Select all
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={clearAll}
              disabled={selectedIds.size === 0}
              style={{ fontSize: 'var(--fs-xs)', padding: '2px 6px' }}
            >
              Clear
            </button>
            <div style={{ flex: 1 }} />
            <Dropdown
              trigger={
                <Button
                  variant="secondary"
                  size="sm"
                  rightIcon="chevron-down"
                  disabled={
                    updateUsersStatus.isLoading || selectedIds.size === 0
                  }
                >
                  Set status
                </Button>
              }
              align="right"
              width={160}
            >
              {STATUS_ORDER.map((key) => (
                <MenuItem key={key} onClick={() => onSetStatus(key)}>
                  {EventUserStatusLabel[key]}
                </MenuItem>
              ))}
            </Dropdown>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setRemoveConfirmOpen(true)}
              disabled={removeUsersStatus.isLoading || selectedIds.size === 0}
            >
              Remove
            </Button>
          </div>
        </div>
      )}

      {/* Attendee list */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 12 }}>
        {list.length === 0 ? (
          <EmptyState
            icon="users"
            title="No attendees yet"
            desc='Click "Add attendees" to add some.'
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 10,
            }}
          >
            {list.map((eu) => (
              <AttendeeCard
                key={eu.id}
                attendee={eu}
                seat={seatMap.get(eu.userId)?.seat}
                selected={selectedIds.has(eu.id)}
                onToggle={() => toggleOne(eu.id)}
                onView={() => {
                  setViewEventUserId(eu.id);
                  setDetailOpen(true);
                }}
                onRemove={() => {
                  if (window.confirm('Remove this attendee?')) {
                    removeEventUser({
                      eventId: safeEventId,
                      eventUserId: eu.id,
                    });
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add attendees modal */}
      <AddAttendeesModal
        eventId={safeEventId}
        excludeUserIds={excludeUserIds}
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
      />

      {/* Attendee detail modal */}
      <AttendeeDetailModal
        eventUser={attendees?.find((a) => a.id === viewEventUserId) ?? null}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
      />

      {/* Remove confirm modal */}
      <Modal
        open={removeConfirmOpen}
        onClose={() => setRemoveConfirmOpen(false)}
        title={`Remove ${selectedIds.size} attendee${
          selectedIds.size === 1 ? '' : 's'
        }`}
        footer={
          <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setRemoveConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={onConfirmRemove}
              loading={removeUsersStatus.isLoading}
            >
              Remove
            </Button>
          </div>
        }
      >
        This will remove the selected attendees from the event.
      </Modal>
    </div>
  );
};

interface AttendeeCardProps {
  attendee: EventUserEntity;
  seat?: string;
  selected: boolean;
  onToggle: () => void;
  onView: () => void;
  onRemove: () => void;
}

const AttendeeCard = ({
  attendee: eu,
  seat,
  selected,
  onToggle,
  onView,
  onRemove,
}: AttendeeCardProps) => {
  const displayName = getAttendeeDisplayName(eu);
  const hasRealName = !!(eu.eventNickname?.trim() || eu.user?.profile?.name);
  const email = eu.user?.email;
  const status = eu.status as EventUserStatus;
  const gender = eu.user?.profile?.gender;

  return (
    <div
      style={{
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: selected ? 'var(--accent)' : 'var(--border)',
        background: selected ? 'var(--accent-subtle)' : 'var(--bg-canvas)',
        borderRadius: 'var(--radius-md)',
        padding: 10,
      }}
    >
      <div className="row gap-2" style={{ alignItems: 'flex-start' }}>
        <div style={{ marginTop: 2 }}>
          <Checkbox checked={selected} onChange={onToggle} />
        </div>
        {seat && (
          <span
            className="mono"
            style={{
              fontSize: 'var(--fs-xs)',
              fontWeight: 700,
              color: 'var(--accent-text)',
              background: 'var(--accent-subtle)',
              borderRadius: 4,
              padding: '1px 5px',
              marginTop: 2,
              flexShrink: 0,
            }}
          >
            {seat}
          </span>
        )}
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="row gap-1" style={{ alignItems: 'center' }}>
            <span
              style={{
                fontSize: 'var(--fs-sm)',
                fontWeight: 500,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontStyle: hasRealName ? undefined : 'italic',
                color: hasRealName ? undefined : 'var(--text-muted)',
                cursor: 'pointer',
              }}
              onClick={onView}
            >
              {displayName || '(No name)'}
            </span>
            <GenderTag gender={gender} />
          </div>
          {email && (
            <div
              className="text-muted truncate"
              style={{ fontSize: 'var(--fs-xs)', marginTop: 1 }}
            >
              {email}
            </div>
          )}
          <div
            className="row gap-1"
            style={{ marginTop: 4, alignItems: 'center' }}
          >
            <Badge color={STATUS_COLOR[status]} size="sm">
              {EventUserStatusLabel[status]}
            </Badge>
            {eu.selfEnrolledAt && (
              <Tooltip label="Self-enrolled via QR scan">
                <span
                  className="row gap-1 text-muted"
                  style={{ fontSize: 'var(--fs-2xs)', cursor: 'default' }}
                >
                  <Icon name="qr-code" size={11} />
                  via QR
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <button
          className="btn btn-ghost btn-icon btn-sm"
          title="Remove attendee"
          onClick={onRemove}
          style={{ flexShrink: 0 }}
        >
          <Icon name="x" size={14} />
        </button>
      </div>
    </div>
  );
};

export default Attendees;
